import React from 'react';
import { Button } from '@odin-labs/components';
import { UserHardHatIcon } from 'components/icons';
import {
  JobsiteFormSubmissionVisitor,
  FormSubmissionEditableVisitorsProps,
} from './types';
import { FormSubmissionEditableVisitor } from './FormSubmissionEditableVisitor';

export const FormSubmissionEditableVisitors = React.forwardRef<
  HTMLDivElement,
  FormSubmissionEditableVisitorsProps
>((props, ref): React.ReactElement => {
  const { value, onChange, error } = props;

  const errorObject =
    typeof error === 'string'
      ? (JSON.parse(error) as Record<
          number,
          Record<keyof JobsiteFormSubmissionVisitor, string>
        >)
      : null;

  const addVisitor = (): void => {
    const newVisitor: JobsiteFormSubmissionVisitor = {
      id: new Date().valueOf(),
      name: '',
      company: '',
      reasonForVisit: '',
    };
    const newValue = [newVisitor, ...(value ?? [])];
    onChange?.(newValue);
  };

  const updateVisitor = (
    visitor: Partial<JobsiteFormSubmissionVisitor>,
  ): void => {
    const newValue = value.map((v) =>
      v.id !== visitor.id ? v : { ...v, ...visitor },
    );
    onChange?.(newValue);
  };

  const removeVisitor = (visitorId: number): void => {
    const newValue = value.filter((v) => v.id !== visitorId);
    onChange?.(newValue.length ? newValue : null);
  };

  const visitorsRef = React.useRef<
    Record<number, { focusFirstError: () => void }>
  >({});
  const innerRef = React.useRef<HTMLDivElement>();
  React.useImperativeHandle(ref, () => {
    return (
      innerRef.current &&
      Object.assign(innerRef.current, {
        focus() {
          const firstErrorVisitorId = Object.keys(errorObject ?? {})[0];
          const firstErrorVisitor =
            firstErrorVisitorId &&
            visitorsRef.current[Number(firstErrorVisitorId)];
          if (firstErrorVisitor) {
            firstErrorVisitor.focusFirstError();
          }
        },
      })
    );
  });

  return (
    <div ref={innerRef} className="odin-flex odin-flex-col odin-gap-y-9">
      <Button
        text="Add Visitor"
        theme="dashed"
        className="odin-w-full odin-justify-center odin-h-20"
        icon={UserHardHatIcon}
        onClick={addVisitor}
      />
      <div className="odin-flex odin-flex-col odin-gap-y-5">
        {value?.map((visitor) => (
          <FormSubmissionEditableVisitor
            key={visitor.id}
            ref={(visitorElement): void => {
              visitorsRef.current[visitor.id] = visitorElement;
            }}
            visitor={visitor}
            updateVisitor={updateVisitor}
            removeVisitor={removeVisitor}
            error={errorObject?.[visitor.id]}
          />
        ))}
      </div>
    </div>
  );
});
